import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { deleteSchedule, fetchSchedules, saveSchedule } from './repository'
import { runScheduledCatchUp } from './runner'
import type { ScheduledTransaction } from './types'

export function useSchedules() {
  return useQuery({
    queryKey: ['scheduled'] as const,
    queryFn: fetchSchedules,
    staleTime: 30_000,
  })
}

export function useSaveSchedule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: saveSchedule,
    onSuccess: (_void, s) => {
      qc.setQueryData<ScheduledTransaction[]>(['scheduled'], (prev) => {
        if (!prev) return prev
        const rest = prev.filter((x) => x.id !== s.id)
        return [...rest, s].sort((a, b) => a.nextOccurrence.localeCompare(b.nextOccurrence))
      })
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['scheduled'] }),
  })
}

export function useDeleteSchedule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteSchedule,
    onSuccess: (_void, id) => {
      qc.setQueryData<ScheduledTransaction[]>(['scheduled'], (prev) =>
        prev?.filter((x) => x.id !== id),
      )
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['scheduled'] }),
  })
}

/** Once per mount (post-auth shell): fire any due schedules, then
 *  refresh everything the new transactions touch. Mirrors iOS running
 *  ScheduledTransactionRunner on launch. */
export function useScheduledCatchUp() {
  const qc = useQueryClient()
  const ran = useRef(false)
  useEffect(() => {
    if (ran.current) return
    ran.current = true
    runScheduledCatchUp()
      .then((created) => {
        if (created > 0) {
          void qc.invalidateQueries({ queryKey: ['transactions'] })
          void qc.invalidateQueries({ queryKey: ['scheduled'] })
          void qc.invalidateQueries({ queryKey: ['accounts'] })
        }
      })
      .catch((e) => console.warn('scheduled catch-up failed:', e))
  }, [qc])
}
